import Keycloak, {
  KeycloakError,
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakProfile,
} from 'keycloak-js';

const normalize = (value?: string) =>
  typeof value === 'string' ? value.trim() : '';

const rawKeycloakConfig = {
  url: normalize(import.meta.env.VITE_KEYCLOAK_URL),
  realm: normalize(import.meta.env.VITE_KEYCLOAK_REALM),
  clientId: normalize(import.meta.env.VITE_KEYCLOAK_CLIENT_ID),
};

const KEYCLOAK_ENV_KEY_LOOKUP: Record<keyof typeof rawKeycloakConfig, string> =
  {
    url: 'VITE_KEYCLOAK_URL',
    realm: 'VITE_KEYCLOAK_REALM',
    clientId: 'VITE_KEYCLOAK_CLIENT_ID',
  };

const missingKeycloakConfigKeys = Object.entries(rawKeycloakConfig)
  .filter(([, value]) => !value)
  .map(
    ([key]) => KEYCLOAK_ENV_KEY_LOOKUP[key as keyof typeof rawKeycloakConfig],
  );

const hasMissingKeycloakConfig = missingKeycloakConfigKeys.length > 0;

const AUTH_DISABLED = ['true', '1', 'yes'].includes(
  String(import.meta.env.VITE_AUTH_DISABLED ?? '')
    .trim()
    .toLowerCase(),
);

const SHOULD_DISABLE_AUTH = AUTH_DISABLED || hasMissingKeycloakConfig;

let disableReason: string | null = null;
let authDisabled = SHOULD_DISABLE_AUTH;
let mockAuthEnabled = AUTH_DISABLED || hasMissingKeycloakConfig;

if (!AUTH_DISABLED && hasMissingKeycloakConfig) {
  disableReason = `Missing configuration entries: ${missingKeycloakConfigKeys.join(
    ', ',
  )}`;
  console.warn(
    '[Keycloak] Missing configuration entries. Authentication disabled for local development.',
    { missing: missingKeycloakConfigKeys },
  );
} else if (AUTH_DISABLED) {
  disableReason = 'Authentication disabled via VITE_AUTH_DISABLED.';
}

let keycloak: Keycloak | null = null;

if (!SHOULD_DISABLE_AUTH) {
  keycloak = new Keycloak({
    url: rawKeycloakConfig.url,
    realm: rawKeycloakConfig.realm!,
    clientId: rawKeycloakConfig.clientId!,
  });
}
const MOCK_TOKEN_VALUE = 'dev-mock-token';
const MOCK_USER_DETAILS = {
  id: 'local-dev-user',
  username: 'local.dev',
  email: 'local.dev@example.com',
  firstname: 'Local',
  lastname: 'Developer',
  fullname: 'Local Developer',
  roles: ['developer'],
} as const;
const MOCK_PROFILE: KeycloakProfile = {
  id: MOCK_USER_DETAILS.id,
  username: MOCK_USER_DETAILS.username,
  email: MOCK_USER_DETAILS.email,
  firstName: MOCK_USER_DETAILS.firstname,
  lastName: MOCK_USER_DETAILS.lastname,
} as KeycloakProfile;

const MIN_TOKEN_VALIDITY_SECONDS = 30;
const REFRESH_INTERVAL_MS = 20_000;

let refreshIntervalId: ReturnType<typeof setInterval> | null = null;
let mockToken: string | null = null;
let mockAuthenticated = false;
let lastMockMessage: string | null = null;
let sessionExpiredCallback: (() => void) | null = null;

const configuredOnLoad = normalize(import.meta.env.VITE_KEYCLOAK_ON_LOAD);
const KEYCLOAK_DEFAULT_ON_LOAD = (
  configuredOnLoad === 'login-required' ? 'login-required' : 'check-sso'
) as KeycloakInitOptions['onLoad'];

const ensureMockSession = (message?: string) => {
  if (!mockAuthEnabled) {
    mockToken = null;
    mockAuthenticated = false;
    return false;
  }

  if (!mockAuthenticated) {
    mockToken = MOCK_TOKEN_VALUE;
    mockAuthenticated = true;
  }

  if (message && message !== lastMockMessage) {
    console.info('[Keycloak]', message);
    lastMockMessage = message;
  }

  return true;
};

const disableKeycloak = (
  message: string,
  {
    enableMock = false,
    logLevel = 'warn',
  }: { enableMock?: boolean; logLevel?: 'warn' | 'error' | 'info' } = {},
) => {
  const logger = console[logLevel] ?? console.warn;
  if (message !== disableReason) {
    logger('[Keycloak]', message);
  }

  authDisabled = true;
  mockAuthEnabled = enableMock;
  disableReason = message;
  lastMockMessage = null;
  clearRefreshInterval();
  keycloak = null;

  if (enableMock) {
    ensureMockSession();
    return;
  }

  mockToken = null;
  mockAuthenticated = false;
};

const toErrorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : String(error ?? '');

const resolveRedirectUri = (explicit?: string) => {
  if (explicit) {
    return explicit;
  }

  if (typeof window !== 'undefined') {
    const { origin, pathname } = window.location;
    return `${origin}${pathname}`;
  }

  return undefined;
};

const buildLoginUrl = (redirectUri: string) => {
  const authServerUrl = rawKeycloakConfig.url ?? '';
  const realm = rawKeycloakConfig.realm ?? '';
  const clientId = rawKeycloakConfig.clientId ?? '';
  if (!authServerUrl || !realm || !clientId) {
    return null;
  }

  const base = `${authServerUrl}/realms/${encodeURIComponent(
    realm,
  )}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
  });
  return `${base}?${params.toString()}`;
};

const clearRefreshInterval = () => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
};

const hasAuthParamsInUrl = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const queryMatch = /(^|[?&])(code|state|error|session_state|iss)=/i.test(
    window.location.search,
  );
  const hashValue = window.location.hash.replace(/^#/, '');
  const hashMatch = /(^|[?&])(code|state|error|session_state|iss)=/i.test(
    hashValue,
  );
  return queryMatch || hashMatch;
};

const stripAuthParamsFromUrl = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  let changed = false;
  const paramsToDrop = ['code', 'state', 'session_state', 'error', 'iss'];
  paramsToDrop.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  paramsToDrop.forEach((key) => {
    if (hashParams.has(key)) {
      hashParams.delete(key);
      changed = true;
    }
  });

  const cleanHash = hashParams.toString();
  url.hash = cleanHash ? `#${cleanHash}` : '';

  if (changed) {
    window.history.replaceState({}, document.title, url.toString());
  }

  return changed;
};

const scheduleRefresh = () => {
  if (authDisabled || !keycloak) {
    return;
  }

  clearRefreshInterval();

  refreshIntervalId = setInterval(async () => {
    try {
      if (authDisabled || !keycloak) {
        return;
      }
      const activeKeycloak = keycloak;
      if (!activeKeycloak) {
        return;
      }
      const refreshed = await activeKeycloak.updateToken(
        MIN_TOKEN_VALIDITY_SECONDS,
      );
      if (refreshed) {
        console.debug('[Keycloak] token refreshed');
      }
    } catch (error) {
      console.warn('[Keycloak] silent refresh failed', error);
    }
  }, REFRESH_INTERVAL_MS);
};

const init = async (options: KeycloakInitOptions = {}) => {
  if (authDisabled || !keycloak) {
    const message =
      disableReason ??
      (mockAuthEnabled
        ? 'Authentication disabled. Using mock user for local development.'
        : 'Authentication disabled.');

    ensureMockSession(message);
    return mockAuthenticated;
  }

  const baseRedirectUri = resolveRedirectUri(options.redirectUri);
  const hasAuthResponse = hasAuthParamsInUrl();
  const authTimeoutKey = 'kc_safari_auth_timeout';
  const desiredOnLoad = options?.onLoad ?? KEYCLOAK_DEFAULT_ON_LOAD;
  const requiresLoginOnLoad = desiredOnLoad === 'login-required';

  try {
    const silentCheckSsoUri =
      desiredOnLoad === 'check-sso' && !hasAuthResponse
        ? `${window.location.origin}/silent-check-sso.html`
        : undefined;

    const initOptions: KeycloakInitOptions = {
      ...options,
      checkLoginIframe: false,
      redirectUri: baseRedirectUri,
      onLoad: desiredOnLoad,
      ...(silentCheckSsoUri
        ? { silentCheckSsoRedirectUri: silentCheckSsoUri }
        : {}),
    };

    if (import.meta.env.DEV) {
      console.info('[Keycloak] init options', {
        onLoad: initOptions.onLoad,
        redirectUri: initOptions.redirectUri,
        checkLoginIframe: initOptions.checkLoginIframe,
        silentCheckSsoRedirectUri: initOptions.silentCheckSsoRedirectUri,
        hasAuthResponse,
      });
    }

    const initTimeoutMs = 10_000;
    const initPromise = keycloak.init(initOptions);
    const authenticated = await Promise.race([
      initPromise,
      new Promise<'init-timeout'>((resolve) =>
        setTimeout(() => resolve('init-timeout'), initTimeoutMs),
      ),
    ]);

    if (authenticated === 'init-timeout') {
      console.warn(
        requiresLoginOnLoad
          ? '[Keycloak] init timed out, forcing login redirect.'
          : '[Keycloak] init timed out, continuing without login.',
      );
      if (hasAuthResponse) {
        if (typeof window !== 'undefined') {
          if (!window.sessionStorage?.getItem(authTimeoutKey)) {
            window.sessionStorage?.setItem(authTimeoutKey, '1');
            stripAuthParamsFromUrl();
            if (requiresLoginOnLoad && baseRedirectUri) {
              window.location.replace(baseRedirectUri);
              return false;
            }
          }
          window.sessionStorage?.removeItem(authTimeoutKey);
        }
      }

      if (requiresLoginOnLoad && baseRedirectUri) {
        const loginUrl = buildLoginUrl(baseRedirectUri);
        if (loginUrl) {
          window.location.assign(loginUrl);
        }
      }
      return false;
    }

    if (authenticated) {
      scheduleRefresh();
      if (hasAuthResponse && typeof window !== 'undefined') {
        window.sessionStorage?.removeItem(authTimeoutKey);
      }
      if (hasAuthParamsInUrl()) {
        stripAuthParamsFromUrl();
      }
    } else {
      clearRefreshInterval();
      if (hasAuthParamsInUrl()) {
        stripAuthParamsFromUrl();
      }
      if (requiresLoginOnLoad && baseRedirectUri) {
        const loginUrl = buildLoginUrl(baseRedirectUri);
        if (loginUrl) {
          window.location.assign(loginUrl);
          return false;
        }
      }
    }

    return Boolean(authenticated);
  } catch (error) {
    const resolveInitErrorMessage = (err: unknown) => {
      if (err && typeof err === 'object') {
        const record = err as Record<string, unknown>;
        const errCode =
          typeof record.error === 'string' ? record.error : undefined;
        const errDesc =
          typeof record.error_description === 'string'
            ? record.error_description
            : undefined;
        const errMessage =
          typeof record.message === 'string' ? record.message : undefined;
        const response = record.response as
          | { status?: number; statusText?: string }
          | undefined;
        const responseStatus =
          typeof response?.status === 'number' ? response.status : undefined;
        const responseText =
          typeof response?.statusText === 'string'
            ? response.statusText
            : undefined;

        const parts = [errCode, errDesc, errMessage]
          .filter(Boolean)
          .map((part) => String(part).trim())
          .filter(Boolean);

        if (responseStatus != null) {
          parts.push(
            `HTTP ${responseStatus}${responseText ? ` ${responseText}` : ''}`,
          );
        }

        if (parts.length) {
          return parts.join(' | ');
        }
      }

      return toErrorMessage(err);
    };

    const message = resolveInitErrorMessage(error);
    const lowerMessage = message.toLowerCase();
    const isLoginRequired = lowerMessage.includes('login_required');
    const clientIdMissing =
      lowerMessage.includes('clientid') && lowerMessage.includes('missing');

    if (isLoginRequired) {
      console.info(
        '[Keycloak] No active session (login_required). Continuing as unauthenticated.',
      );
      if (hasAuthParamsInUrl()) {
        stripAuthParamsFromUrl();
      }
      return false;
    }

    const loggedError =
      error ?? new Error('Keycloak init failed without an error payload.');
    console.error('[Keycloak] init failed', loggedError, {
      reason: message,
    });

    const failureReason = clientIdMissing
      ? 'Keycloak initialization failed: clientId missing. Authentication disabled.'
      : `Keycloak initialization failed: ${message}. Authentication disabled.`;

    if (clientIdMissing) {
      disableKeycloak(failureReason, {
        enableMock: false,
        logLevel: 'error',
      });
      return false;
    }

    if (hasAuthResponse && typeof window !== 'undefined') {
      window.sessionStorage?.removeItem(authTimeoutKey);
      stripAuthParamsFromUrl();
    }

    if (requiresLoginOnLoad && baseRedirectUri) {
      const loginUrl = buildLoginUrl(baseRedirectUri);
      if (loginUrl) {
        window.location.assign(loginUrl);
      }
    }

    return false;
  }
};

const login = async (options: KeycloakLoginOptions = {}) => {
  if (authDisabled || !keycloak) {
    const mocked = ensureMockSession(
      disableReason ??
        'Authentication disabled. Using mock user for local development.',
    );

    if (mocked) {
      console.info('[Keycloak] login skipped (auth disabled)');
    } else {
      console.warn(
        '[Keycloak] login unavailable because authentication is disabled.',
        disableReason ?? '',
      );
    }
    return;
  }

  const redirectUri = resolveRedirectUri(options.redirectUri);

  await keycloak.login({
    ...options,
    ...(redirectUri ? { redirectUri } : {}),
  });
};

const logout = async () => {
  clearRefreshInterval();
  if (authDisabled || !keycloak) {
    mockAuthenticated = false;
    mockToken = null;
    console.info('[Keycloak] logout skipped (auth disabled)');
    return;
  }

  const redirectUri = resolveRedirectUri();
  await keycloak.logout({
    ...(redirectUri ? { redirectUri } : {}),
  });
};

const updateToken = async () => {
  if (authDisabled || !keycloak) {
    return mockAuthenticated ? mockToken : null;
  }

  const activeKeycloak = keycloak;
  if (!activeKeycloak) {
    return mockAuthenticated ? mockToken : null;
  }

  const refreshed = await activeKeycloak.updateToken(
    MIN_TOKEN_VALIDITY_SECONDS,
  );
  return refreshed
    ? (activeKeycloak.token ?? null)
    : (activeKeycloak.token ?? null);
};

const ensureToken = async () => {
  if (authDisabled || !keycloak) {
    return mockAuthenticated ? mockToken : null;
  }

  const activeKeycloak = keycloak;
  if (!activeKeycloak || !activeKeycloak.token) {
    return null;
  }

  try {
    const refreshedToken = await updateToken();
    return refreshedToken ?? activeKeycloak.token ?? null;
  } catch (error) {
    console.warn('[Keycloak] failed to ensure token', error);
    return null;
  }
};

const getToken = () => {
  if (authDisabled || !keycloak) {
    return mockToken;
  }
  const activeKeycloak = keycloak;
  return activeKeycloak?.token ?? null;
};

const getUserProfile = async (): Promise<KeycloakProfile | null> => {
  if (authDisabled || !keycloak) {
    return mockAuthenticated ? MOCK_PROFILE : null;
  }

  try {
    const activeKeycloak = keycloak;
    if (!activeKeycloak || !activeKeycloak.authenticated) {
      return null;
    }

    return await activeKeycloak.loadUserProfile();
  } catch (error) {
    console.warn('[Keycloak] failed to load profile', error);
    return null;
  }
};

const getUserFromToken = () => {
  if (authDisabled || !keycloak) {
    return mockAuthenticated ? { ...MOCK_USER_DETAILS } : null;
  }

  const activeKeycloak = keycloak;
  const token = activeKeycloak?.tokenParsed;

  if (!token) {
    return null;
  }

  const realmRoles = token.realm_access?.roles ?? [];
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
  const resourceAccess = (token.resource_access ?? {}) as Record<
    string,
    { roles?: string[] }
  >;
  const clientRoles = clientId
    ? (resourceAccess[clientId]?.roles ?? [])
    : Object.values(resourceAccess).flatMap((access) => access.roles ?? []);
  const roles = Array.from(new Set([...realmRoles, ...clientRoles]));

  return {
    id: token.sub,
    username: token.preferred_username,
    email: token.email,
    firstname: token.given_name,
    lastname: token.family_name,
    fullname:
      token.name ??
      `${token.given_name ?? ''} ${token.family_name ?? ''}`.trim(),
    roles,
  };
};

if (!authDisabled && keycloak) {
  keycloak.onTokenExpired = async () => {
    if (authDisabled) {
      return;
    }

    const activeKeycloak = keycloak;
    if (!activeKeycloak) {
      return;
    }

    try {
      await activeKeycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS);
      console.debug('[Keycloak] token refreshed after expiry event');
    } catch (error) {
      console.warn(
        '[Keycloak] token refresh on expiry failed, clearing local session',
        error as KeycloakError,
      );
      clearRefreshInterval();
      sessionExpiredCallback?.();
    }
  };
}

const forceTokenRefresh = async (): Promise<boolean> => {
  if (authDisabled || !keycloak) {
    return mockAuthenticated;
  }

  const activeKeycloak = keycloak;
  if (!activeKeycloak?.authenticated) {
    return false;
  }

  // -1 forces a refresh regardless of remaining token lifetime
  const refreshed = await activeKeycloak.updateToken(-1);
  return refreshed;
};

const KeycloakService = {
  get instance() {
    return keycloak;
  },
  get isAuthDisabled() {
    return authDisabled;
  },
  get disableReason() {
    return disableReason;
  },
  init,
  login,
  logout,
  getToken,
  ensureToken,
  updateToken,
  getUserFromToken,
  getUserProfile,
  clearRefreshInterval,
  forceTokenRefresh,
  setSessionExpiredCallback(cb: () => void) {
    sessionExpiredCallback = cb;
  },
};

export type KeycloakUser = ReturnType<typeof getUserFromToken>;

export default KeycloakService;
